"use client";

import { useState, type ComponentProps } from "react";

import { useResolveI18n } from "./resolveI18n";

import { cn } from "@/lib/utils/cn";

type BreakdownItem = {
  label: string;
  count: number;
};

type BreakdownProps = Omit<ComponentProps<"div">, "children" | "title"> & {
  title?: string;
  items: readonly BreakdownItem[];
  total: number;
  initialCount?: number;
};

function BarRow({
  item,
  maxCount,
  index,
  resolve,
}: {
  item: BreakdownItem;
  maxCount: number;
  index: number;
  resolve: (v: string) => string;
}) {
  const pct = maxCount > 0 ? (item.count / maxCount) * 100 : 0;

  return (
    <div
      className="flex items-center gap-3 animate-in fade-in fill-mode-both duration-300"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <span className="w-32 shrink-0 truncate text-xs text-muted-foreground">
        {resolve(item.label)}
      </span>
      <div className="relative flex h-6 min-w-0 flex-1 items-center">
        <div
          className="absolute inset-y-0 left-0 rounded bg-primary/15"
          style={{ width: `${Math.max(pct, 2)}%` }}
        />
        <span className="relative z-10 pl-2 text-xs font-medium tabular-nums text-foreground">
          {item.count}
        </span>
      </div>
    </div>
  );
}

export function Breakdown({
  title,
  items,
  total,
  initialCount = 10,
  className,
  ...props
}: BreakdownProps) {
  const resolve = useResolveI18n();
  const [expanded, setExpanded] = useState(false);
  const maxCount = items.length > 0 ? items[0]!.count : 0;
  const hasOverflow = items.length > initialCount;
  const visible = expanded ? items : items.slice(0, initialCount);
  const hiddenCount = items.length - initialCount;

  return (
    <div
      data-slot="panel"
      className={cn(
        "flex w-full flex-col gap-2 rounded-xl border border-border bg-card p-4",
        className,
      )}
      {...props}
    >
      {title && (
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-semibold text-foreground">{title}</span>
          <span className="text-xs tabular-nums text-muted-foreground">
            {total} total
          </span>
        </div>
      )}

      <div className="flex flex-col gap-1">
        {visible.map((item, i) => (
          <BarRow key={item.label} item={item} maxCount={maxCount} index={i} resolve={resolve} />
        ))}
      </div>

      {hasOverflow && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-left text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          {expanded
            ? "Show less"
            : `+ ${hiddenCount} more`}
        </button>
      )}
    </div>
  );
}
